import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { aboutUsAPI } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { 
  Users, 
  Globe, 
  MessageSquare, 
  Target, 
  Eye, 
  Save, 
  Loader2,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import ParticlesBackground from '../components/ParticlesBackground';
import {
  AboutContainer,
  FloatingElement,
  MainContent,
  SectionHeader,
  Title,
  Subtitle,
  Form,
  FormSection,
  SectionTitle,
  InputGroup,
  Label,
  TextArea,
  Input,
  StatsGrid,
  StatCard,
  StatIcon,
  StatValue,
  StatLabel,
  SubmitButton,
  LoadingSpinner,
  ErrorMessage,
  SuccessMessage,
  PreviewSection,
  PreviewTitle,
  PreviewCard,
  PreviewContent,
  PreviewStats,
  PreviewStat,
  PreviewStatValue,
  PreviewStatLabel
} from './AboutSection.styles';

const AboutSection = () => {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const [formData, setFormData] = useState({
    opening_paragraph: '',
    mission: '',
    vision: '',
    clients_served: '',
    countries: '',
    messages_delivered: '',
    team_members: ''
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [existingData, setExistingData] = useState(null);

  // Load existing data on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const response = await aboutUsAPI.get();
        if (response.status === 200) {
          const data = response.data;
          setExistingData(data);
          setFormData({
            opening_paragraph: data.opening_paragraph || '',
            mission: data.mission || '',
            vision: data.vision || '',
            clients_served: data.clients_served || '',
            countries: data.countries || '',
            messages_delivered: data.messages_delivered || '',
            team_members: data.team_members || ''
          });
        }
      } catch (error) {
        if (error.response?.status !== 404) {
          const errorMessage = error.response?.data?.message || 'Failed to load about us data. Please try again.';
          showError(errorMessage);
        }
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.opening_paragraph.trim() || !formData.mission.trim() || !formData.vision.trim()) {
      setError('Please fill in all required fields (Opening Paragraph, Mission, and Vision).');
      return;
    }

    try {
      setSaving(true);
      setError('');

      // Convert string values to numbers for statistics
      const submitData = {
        ...formData,
        clients_served: formData.clients_served ? parseInt(formData.clients_served) : 0,
        countries: formData.countries ? parseInt(formData.countries) : 0,
        messages_delivered: formData.messages_delivered ? parseInt(formData.messages_delivered) : 0,
        team_members: formData.team_members ? parseInt(formData.team_members) : 0
      };

      const response = existingData 
        ? await aboutUsAPI.update(submitData)
        : await aboutUsAPI.create(submitData);

      if (response.status === 200 || response.status === 201) {
        showSuccess(existingData ? 'About us section updated successfully!' : 'About us section created successfully!');
        setExistingData(response.data);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to save about us section. Please try again.';
      showError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AboutContainer>
        <MainContent>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            minHeight: '400px',
            flexDirection: 'column',
            gap: '1rem'
          }}>
            <LoadingSpinner />
            <p style={{ color: '#64748B', fontSize: '1rem' }}>Loading about us data...</p>
          </div>
        </MainContent>
      </AboutContainer>
    );
  }

  return (
    <AboutContainer>
      <ParticlesBackground />
      
      <FloatingElement delay="0s" />
      <FloatingElement delay="3s" />
      <FloatingElement delay="6s" />

      <MainContent>
        <SectionHeader>
          <Title>About Us Section</Title>
          <Subtitle>
            Manage your company's about us content, including mission, vision, and key statistics.
          </Subtitle>
        </SectionHeader>

        {error && (
          <ErrorMessage>
            <AlertCircle size={20} />
            {error}
          </ErrorMessage>
        )}


        <Form onSubmit={handleSubmit}>
          <FormSection>
            <SectionTitle>Content</SectionTitle>
            
            <InputGroup>
              <Label htmlFor="opening_paragraph">Opening Paragraph *</Label>
              <TextArea
                id="opening_paragraph"
                name="opening_paragraph"
                value={formData.opening_paragraph}
                onChange={handleInputChange}
                placeholder="Write a compelling introduction paragraph that describes your company..."
                rows={4}
                required
              />
            </InputGroup>

            <InputGroup>
              <Label htmlFor="mission">Mission Statement *</Label>
              <TextArea
                id="mission"
                name="mission"
                value={formData.mission}
                onChange={handleInputChange}
                placeholder="Describe your company's mission - what you aim to achieve..."
                rows={3}
                required
              />
            </InputGroup>

            <InputGroup>
              <Label htmlFor="vision">Vision Statement *</Label>
              <TextArea
                id="vision"
                name="vision"
                value={formData.vision}
                onChange={handleInputChange}
                placeholder="Describe your company's vision - where you want to be in the future..."
                rows={3}
                required
              />
            </InputGroup>
          </FormSection>

          <FormSection>
            <SectionTitle>Statistics</SectionTitle>
            
            <StatsGrid>
              <StatCard>
                <StatIcon color="#C5337B">
                  <Users size={24} />
                </StatIcon>
                <div>
                  <Label htmlFor="clients_served">Clients Served</Label>
                  <Input
                    id="clients_served"
                    name="clients_served"
                    type="number"
                    value={formData.clients_served}
                    onChange={handleInputChange}
                    min="0"
                  />
                </div>
              </StatCard>

              <StatCard>
                <StatIcon color="#E91E63">
                  <Globe size={24} />
                </StatIcon>
                <div>
                  <Label htmlFor="countries">Countries</Label>
                  <Input
                    id="countries"
                    name="countries"
                    type="number"
                    value={formData.countries}
                    onChange={handleInputChange}
                    min="0"
                  />
                </div>
              </StatCard>

              <StatCard>
                <StatIcon color="#F06292">
                  <MessageSquare size={24} />
                </StatIcon>
                <div>
                  <Label htmlFor="messages_delivered">Messages Delivered</Label>
                  <Input
                    id="messages_delivered"
                    name="messages_delivered"
                    type="number"
                    value={formData.messages_delivered}
                    onChange={handleInputChange}
                    min="0"
                  />
                </div>
              </StatCard>

              <StatCard>
                <StatIcon color="#C5337B">
                  <Users size={24} />
                </StatIcon>
                <div>
                  <Label htmlFor="team_members">Team Members</Label>
                  <Input
                    id="team_members"
                    name="team_members"
                    type="number"
                    value={formData.team_members}
                    onChange={handleInputChange}
                    min="0"
                  />
                </div>
              </StatCard>
            </StatsGrid>
          </FormSection>

          <SubmitButton type="submit" disabled={saving}>
            {saving ? (
              <>
                <LoadingSpinner />
                {existingData ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              <>
                <Save size={20} />
                {existingData ? 'Update About Us' : 'Create About Us'}
              </>
            )}
          </SubmitButton>
        </Form>

        {existingData && (
          <PreviewSection>
            <PreviewTitle>Preview</PreviewTitle>
            <PreviewCard>
              <PreviewContent>
                <h3 style={{ color: '#2B3857', marginBottom: '1rem' }}>About Us</h3>
                <p style={{ marginBottom: '1.5rem', lineHeight: '1.6' }}>
                  {formData.opening_paragraph}
                </p>
                
                <div style={{ marginBottom: '1.5rem' }}>
                  <h4 style={{ color: '#C5337B', marginBottom: '0.5rem' }}>
                    <Target size={16} style={{ marginRight: '0.5rem', display: 'inline' }} />
                    Our Mission
                  </h4>
                  <p style={{ lineHeight: '1.5' }}>{formData.mission}</p>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <h4 style={{ color: '#C5337B', marginBottom: '0.5rem' }}>
                    <Eye size={16} style={{ marginRight: '0.5rem', display: 'inline' }} />
                    Our Vision
                  </h4>
                  <p style={{ lineHeight: '1.5' }}>{formData.vision}</p>
                </div>

                <PreviewStats>
                  <PreviewStat>
                    <PreviewStatValue>{formData.clients_served ? parseInt(formData.clients_served).toLocaleString() : '0'}</PreviewStatValue>
                    <PreviewStatLabel>Clients Served</PreviewStatLabel>
                  </PreviewStat>
                  <PreviewStat>
                    <PreviewStatValue>{formData.countries || '0'}</PreviewStatValue>
                    <PreviewStatLabel>Countries</PreviewStatLabel>
                  </PreviewStat>
                  <PreviewStat>
                    <PreviewStatValue>{formData.messages_delivered ? parseInt(formData.messages_delivered).toLocaleString() : '0'}</PreviewStatValue>
                    <PreviewStatLabel>Messages Delivered</PreviewStatLabel>
                  </PreviewStat>
                  <PreviewStat>
                    <PreviewStatValue>{formData.team_members || '0'}</PreviewStatValue>
                    <PreviewStatLabel>Team Members</PreviewStatLabel>
                  </PreviewStat>
                </PreviewStats>
              </PreviewContent>
            </PreviewCard>
          </PreviewSection>
        )}
      </MainContent>
    </AboutContainer>
  );
};

export default AboutSection;
